/**
 * 計助ダッシュボード: 入力形式ごとの表データ ⇔ 内部 gridData（1行1件・長形式）の変換
 * - t検定2列 / 1要因ANOVA列 / 2要因ANOVAブロック など
 */

import {
  type AnalysisOptions,
  type DataRow,
  type InputFormTypeKey,
  type VariableMapping,
  clampKeisukeGroupObservationCount,
  clampKeisukeOneWayGroupCount,
} from '../stores/systems/keisuke/dashboardStore';
import type { StandardDatasetEntry } from '../services/systems/keisuke/keisukeStandardDatasets';
import { trimTrailingEmptyGridRows } from './keisukeGridData';

/**
 * 数値入力向け: 全角数字（０-９）と全角の小数点・ハイフンマイナス・カンマを半角に置換する。
 * 貼り付け・キー入力の両方から呼ぶ。
 */
export function normalizeKeisukeNumericInput(value: string): string {
  return value
    .replace(/[\uFF10-\uFF19]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0))
    .replace(/\uFF0E/g, '.')
    .replace(/\uFF0D/g, '-')
    .replace(/\u2212/g, '-')
    .replace(/\uFF0C/g, ',');
}

/** 列名変更時に左ペインのマッピングを追従させるキー */
const MAPPING_KEYS_FOR_COLUMN_RENAME: (keyof VariableMapping)[] = [
  'groupColumn',
  'valueColumn',
  'beforeColumn',
  'afterColumn',
  'factorAColumn',
  'factorBColumn',
  'subjectColumn',
  'doseColumn',
  'effectColumn',
  'totalColumn',
  'timeColumn',
  'eventColumn',
  'survivalGroupColumn',
];

/** 他列と重複しない列名（必要なら `_2`, `_3` を付与） */
export function makeUniqueGridColumnName(
  candidate: string,
  headers: string[],
  exceptIndex: number
): string {
  const base = candidate.trim() || 'Column';
  const taken = (name: string) => headers.some((h, i) => i !== exceptIndex && h === name);
  if (!taken(base)) return base;
  let n = 2;
  while (taken(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

export type RenameGridColumnResult =
  | { ok: false; reason: 'empty' | 'unchanged' }
  | {
      ok: true;
      gridHeaders: string[];
      gridData: DataRow[];
      /** マッピングを更新したときのみ（setAnalysisOptions にそのまま渡す） */
      mapping?: VariableMapping;
    };

/**
 * 長形式グリッドの1列の表示名（＝行オブジェクトのキー）を変更する。
 * 左ペインでその列を指していた mapping も同じ新名に置き換える。
 */
export function renameGridColumnAtIndex(
  columnIndex: number,
  rawNewName: string,
  gridHeaders: string[],
  gridData: DataRow[],
  mapping: VariableMapping | undefined
): RenameGridColumnResult {
  const trimmed = rawNewName.trim();
  if (trimmed === '') return { ok: false, reason: 'empty' };

  const oldName = gridHeaders[columnIndex];
  if (oldName == null) return { ok: false, reason: 'empty' };

  const unique = makeUniqueGridColumnName(trimmed, gridHeaders, columnIndex);
  if (unique === oldName) return { ok: false, reason: 'unchanged' };

  const newHeaders = gridHeaders.map((h, i) => (i === columnIndex ? unique : h));
  const newData = gridData.map((row) => {
    const copy = { ...row };
    if (Object.prototype.hasOwnProperty.call(copy, oldName)) {
      copy[unique] = copy[oldName];
      delete copy[oldName];
    }
    return copy;
  });

  if (!mapping) {
    return { ok: true, gridHeaders: newHeaders, gridData: newData };
  }

  const nextMapping: VariableMapping = { ...mapping };
  let changed = false;
  for (const k of MAPPING_KEYS_FOR_COLUMN_RENAME) {
    if (mapping[k] === oldName) {
      (nextMapping as Record<string, string | undefined>)[k as string] = unique;
      changed = true;
    }
  }

  return {
    ok: true,
    gridHeaders: newHeaders,
    gridData: newData,
    ...(changed ? { mapping: nextMapping } : {}),
  };
}

/**
 * 解析タイプに対して UI 上選べる入力形式（ツールバーのセレクト用）。
 *
 * 表示上の対応（中央ペインは `KeisukeDashboardUnifiedInputGrid` 1本。レイアウト種別は
 * `dashboard/keisukeDashboardDataGridLayout.ts` の `getKeisukeDashboardDataGridLayout`）:
 * - 長形式系 … スタック表＋仮想スクロール
 * - t検定2列 / 1要因列 / 2要因ブロック … 列行列またはブロック表（ストアは同じ gridData へ変換）
 *
 * サンプル読込（`KeisukeDashboardCenterPane` の `handleLoadSampleData`）は、このいずれかに収まるよう `setInputFormType` し、
 * `getStandardDatasetLayoutPatch` でストアの行数・列数・2要因ブロック寸法をデータに合わせる。
 * 1要因ANOVA の標準サンプルは `oneWayColumns` を既定とし、群名が表の1行目に入力済みになる。
 *
 * 配列の先頭が既定（`coerceInputFormTypeToMatchAnalysis` のフォールバック）。長形式（1行1件）は補助的で末尾に置く。
 */
export function getAllowedInputFormTypesForAnalysis(
  opts: Pick<AnalysisOptions, 'type' | 'anovaType'>
): InputFormTypeKey[] {
  const raw = opts.type as string;
  const tn = raw === '' || raw == null ? 't-test' : raw;
  const at = (opts.anovaType as string) ?? 'oneWay';
  if (tn === 't-test') return ['tTestTwoColumns', 'longFormat'];
  if (tn === 'anova' && at === 'twoWay') return ['twoWayBlocks'];
  if (tn === 'anova' && at === 'repeatedMeasures') return ['repeatedForm', 'longFormat'];
  if (tn === 'anova') return ['oneWayColumns', 'longFormat'];
  if (tn === 'dose-response') return ['doseResponseForm', 'longFormat'];
  if (tn === 'survival') return ['survivalForm', 'longFormat'];
  if (tn === 'basic-stats' || tn === 'qpcr' || tn === 'longitudinal' || tn === 'regression' || tn === 'bar-chart') {
    return ['longFormat'];
  }
  return ['longFormat'];
}

/**
 * 解析タイプと両立しない入力形式を直す（例: 2要因から1要因へ切替後も twoWayBlocks のまま、など）
 */
export function coerceInputFormTypeToMatchAnalysis(
  inputFormType: InputFormTypeKey,
  opts: Pick<AnalysisOptions, 'type' | 'anovaType'>
): InputFormTypeKey {
  const allowed = getAllowedInputFormTypesForAnalysis(opts);
  if (allowed.includes(inputFormType)) return inputFormType;
  return allowed[0];
}

const GROUP_KEY = 'group';
const VALUE_KEY = 'value';

/** 数値文字列をパース。空・非数は null */
export function parseNumber(s: string): number | null {
  if (s == null || String(s).trim() === '') return null;
  const n = Number(String(s).trim());
  return Number.isFinite(n) ? n : null;
}

function trimLabel(s: string | undefined, fallback: string): string {
  const t = (s ?? '').trim();
  return t || fallback;
}

/** t検定用 2列フォーム: 1行目=項目ラベル、2行目以降=数値 */
export interface TTestTwoColumnForm {
  labelColA: string;
  labelColB: string;
  /** 2行目以降の値。各行は [列Aの値, 列Bの値]。空は未入力として無視 */
  valueRows: [string, string][];
}

/** 対応なし: 2列を群A・群Bとして長形式（group, value）に変換 */
export function tTestTwoColumnFormToGridData(
  form: TTestTwoColumnForm,
  keys?: { groupKey?: string; valueKey?: string }
): {
  headers: string[];
  data: DataRow[];
} {
  const groupKey = keys?.groupKey?.trim() || GROUP_KEY;
  const valueKey = keys?.valueKey?.trim() || VALUE_KEY;
  const headers = [groupKey, valueKey];
  const labelA = trimLabel(form.labelColA, 'GroupA');
  const labelB = trimLabel(form.labelColB, 'GroupB');
  const data: DataRow[] = [];
  form.valueRows.forEach(([a, b]) => {
    const va = parseNumber(a);
    const vb = parseNumber(b);
    if (va !== null) data.push({ [groupKey]: labelA, [valueKey]: va });
    if (vb !== null) data.push({ [groupKey]: labelB, [valueKey]: vb });
  });
  return { headers, data };
}

/** 対応あり: 同じ行の2列を Before/After として1行1対の形式に変換 */
export function tTestTwoColumnFormToGridDataPaired(form: TTestTwoColumnForm): {
  headers: string[];
  data: DataRow[];
} {
  const beforeCol = trimLabel(form.labelColA, 'Before');
  const afterCol = trimLabel(form.labelColB, 'After');
  const headers = [beforeCol, afterCol];
  const data: DataRow[] = form.valueRows
    .map(([a, b]) => {
      const va = parseNumber(a);
      const vb = parseNumber(b);
      if (va === null || vb === null) return null;
      return { [beforeCol]: va, [afterCol]: vb } as DataRow;
    })
    .filter((r): r is DataRow => r !== null);
  return { headers, data };
}

/** 長形式 gridData を t検定2列フォームに変換可能か判定し、変換（対応なし用） */
export function gridDataToTTestTwoColumnForm(
  data: DataRow[],
  headers: string[],
  groupCol?: string,
  valueCol?: string
): TTestTwoColumnForm | null {
  const gCol = groupCol ?? headers[0];
  const vCol = valueCol ?? headers[1];
  if (!gCol || !vCol || headers.length < 2) return null;
  const groups = [...new Set(data.map((r) => String(r[gCol] ?? '').trim()).filter(Boolean))];
  /**
   * 途中入力は1群だけのことがある。2群必須にすると空フォームへ落ちてセルが消える。
   * 0群は未入力。3群以上は2列UIの対象外。
   */
  if (groups.length === 0 || groups.length > 2) return null;
  const labelA = groups[0]!;
  const labelB = groups[1] ?? '';
  const valuesA = data.filter((r) => String(r[gCol] ?? '').trim() === labelA).map((r) => r[vCol]);
  const valuesB = labelB
    ? data.filter((r) => String(r[gCol] ?? '').trim() === labelB).map((r) => r[vCol])
    : [];
  const maxRows = Math.max(valuesA.length, valuesB.length, 1);
  const valueRows: [string, string][] = [];
  for (let i = 0; i < maxRows; i++) {
    valueRows.push([
      valuesA[i] != null && String(valuesA[i]).trim() !== '' ? String(valuesA[i]) : '',
      valuesB[i] != null && String(valuesB[i]).trim() !== '' ? String(valuesB[i]) : '',
    ]);
  }
  return { labelColA: labelA, labelColB: labelB, valueRows };
}

/** 1行1対形式（Before/After）を t検定2列フォームに変換（対応あり用） */
export function gridDataToTTestTwoColumnFormPaired(
  data: DataRow[],
  headers: string[],
  beforeCol?: string,
  afterCol?: string
): TTestTwoColumnForm | null {
  const bCol = beforeCol ?? headers[0];
  const aCol = afterCol ?? headers[1];
  if (!bCol || !aCol || headers.length < 2) return null;
  /** 長形式が万行パディングされているとき、末尾の空行だけ除いてから行数を合わせる */
  const trimmed = trimTrailingEmptyGridRows(headers, data);
  const valueRows: [string, string][] = trimmed.map((r) => [
    r[bCol] != null && String(r[bCol]).trim() !== '' ? String(r[bCol]) : '',
    r[aCol] != null && String(r[aCol]).trim() !== '' ? String(r[aCol]) : '',
  ]);
  return {
    labelColA: bCol,
    labelColB: aCol,
    valueRows,
  };
}

/** 1要因ANOVA用 列フォーム: 1行目=各群のラベル、2行目以降=各列の値。空列はその群を使わない */
export interface OneWayColumnForm {
  /** 各列の群ラベル。空文字の列は無視 */
  columnLabels: string[];
  /** 2行目以降。valueRows[r][c] = 行r・列c の値。空は無視 */
  valueRows: string[][];
}

export function oneWayColumnFormToGridData(form: OneWayColumnForm): {
  headers: string[];
  data: DataRow[];
} {
  const headers = [GROUP_KEY, VALUE_KEY];
  const data: DataRow[] = [];
  const labels = form.columnLabels;
  form.valueRows.forEach((row) => {
    row.forEach((cell, c) => {
      const label = labels[c];
      if (label == null || String(label).trim() === '') return;
      const v = parseNumber(cell);
      if (v !== null) data.push({ [GROUP_KEY]: label.trim(), [VALUE_KEY]: v });
    });
  });
  return { headers, data };
}

/** 長形式を 1要因ANOVA 列フォームに変換（群ごとに列を分ける） */
export function gridDataToOneWayColumnForm(
  data: DataRow[],
  headers: string[],
  groupCol?: string,
  valueCol?: string
): OneWayColumnForm | null {
  const gCol = groupCol ?? headers[0];
  const vCol = valueCol ?? headers[1];
  if (!gCol || !vCol) return null;
  const groups = [...new Set(data.map((r) => String(r[gCol] ?? '').trim()).filter(Boolean))];
  if (groups.length === 0) return null;
  const columnLabels = groups;
  const byGroup = new Map<string, (number | string)[]>();
  groups.forEach((g) => byGroup.set(g, []));
  data.forEach((r) => {
    const g = String(r[gCol] ?? '').trim();
    const val = r[vCol];
    if (g && byGroup.has(g)) byGroup.get(g)!.push(val ?? '');
  });
  const maxRows = Math.max(0, ...groups.map((g) => byGroup.get(g)!.length));
  const valueRows: string[][] = [];
  for (let r = 0; r < maxRows; r++) {
    valueRows.push(groups.map((g) => (byGroup.get(g)![r] != null ? String(byGroup.get(g)![r]) : '')));
  }
  return { columnLabels, valueRows };
}

/** 2要因ANOVA用 ブロックフォーム: 1群あたり n 件のデータをブロックでまとめる */
export interface TwoWayBlockForm {
  /** 1セル（1群）あたりのデータ数 */
  nPerCell: number;
  /** 因子Aの列名 */
  factorAColumn: string;
  /** 因子Bの列名 */
  factorBColumn: string;
  /** 値の列名 */
  valueColumn: string;
  /**
   * 行: 各セルブロック。blocks[b] = そのセルの nPerCell 個の値。
   * ブロックの順序は因子の組み合わせ（例: A1-B1, A1-B2, A2-B1, A2-B2）。
   * ブロック識別は factorALabel, factorBLabel で行う。
   */
  blocks: Array<{ factorALabel: string; factorBLabel: string; values: string[] }>;
}

export function twoWayBlockFormToGridData(form: TwoWayBlockForm): {
  headers: string[];
  data: DataRow[];
} {
  const headers = [form.factorAColumn, form.factorBColumn, form.valueColumn];
  const data: DataRow[] = [];
  form.blocks.forEach((block) => {
    block.values.forEach((v) => {
      const num = parseNumber(v);
      if (num !== null) {
        data.push({
          [form.factorAColumn]: block.factorALabel,
          [form.factorBColumn]: block.factorBLabel,
          [form.valueColumn]: num,
        });
      }
    });
  });
  return { headers, data };
}

/** 長形式を 2要因ブロックフォームに変換（nPerCell はセルごとのデータ数から推定） */
export function gridDataToTwoWayBlockForm(
  data: DataRow[],
  headers: string[],
  factorACol: string,
  factorBCol: string,
  valueCol: string,
  nPerCell: number
): TwoWayBlockForm | null {
  const key = (a: string, b: string) => `${a}\t${b}`;
  const cellValues = new Map<string, (number | string)[]>();
  data.forEach((r) => {
    const a = String(r[factorACol] ?? '').trim();
    const b = String(r[factorBCol] ?? '').trim();
    const v = r[valueCol];
    if (!a || !b) return;
    const k = key(a, b);
    if (!cellValues.has(k)) cellValues.set(k, []);
    cellValues.get(k)!.push(v ?? '');
  });
  const blocks: TwoWayBlockForm['blocks'] = [];
  const maxObserved = Math.max(
    1,
    ...[...cellValues.values()].map((v) => v.length)
  );
  /** 表示行数は「要求された n」と「各セルの実データ数」の大きい方（不足分は空文字でパディング） */
  const requested = nPerCell > 0 ? nPerCell : maxObserved;
  const targetN = Math.max(requested, maxObserved);
  cellValues.forEach((values, k) => {
    const tabIdx = k.indexOf('\t');
    const factorALabel = tabIdx >= 0 ? k.slice(0, tabIdx) : k;
    const factorBLabel = tabIdx >= 0 ? k.slice(tabIdx + 1) : '';
    let strs = values.map((x) => String(x));
    if (strs.length < targetN) {
      strs = [...strs, ...Array(targetN - strs.length).fill('')];
    }
    blocks.push({ factorALabel, factorBLabel, values: strs });
  });
  /** 列名不一致（例: 1要因の group/value のみ）ではセルが1件も作れない → null でフォールバックUIを出す */
  if (blocks.length === 0) {
    return null;
  }
  return {
    nPerCell: targetN,
    factorAColumn: factorACol,
    factorBColumn: factorBCol,
    valueColumn: valueCol,
    blocks,
  };
}

/** 標準サンプル読込時にストアへ反映する入力レイアウト用の値 */
export type StandardDatasetLayoutPatch = {
  tTestNPerGroup?: number;
  oneWayGroupCount?: number;
  oneWayNPerGroup?: number;
  twoWayNLevelsA?: number;
  twoWayNLevelsB?: number;
  twoWayNPerCell?: number;
  doseResponseNRows?: number;
  survivalNRows?: number;
  repeatedNRows?: number;
};

function inferTwoWayNPerCellFromLongData(data: DataRow[], factorACol: string, factorBCol: string): number {
  const key = (a: string, b: string) => `${a}\t${b}`;
  const counts = new Map<string, number>();
  data.forEach((r) => {
    const a = String(r[factorACol] ?? '').trim();
    const b = String(r[factorBCol] ?? '').trim();
    if (!a || !b) return;
    const k = key(a, b);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  });
  const vals = [...counts.values()];
  if (vals.length === 0) return 3;
  return Math.max(1, Math.min(...vals));
}

/**
 * サンプルデータ読込後に `useKeisukeDashboardStore.setState` へ渡す、表の行数・列数・2要因ブロック寸法。
 */
export function getStandardDatasetLayoutPatch(entry: StandardDatasetEntry): StandardDatasetLayoutPatch {
  const opt = entry.suggestedOptions;
  const st = opt?.type;
  const map = opt?.mapping ?? {};
  const headers = entry.columns;
  const data = entry.data;
  const patch: StandardDatasetLayoutPatch = {};

  if (st === 't-test') {
    const testType = opt?.testType ?? 'unpaired';
    if (testType === 'paired') {
      const f = gridDataToTTestTwoColumnFormPaired(
        data,
        headers,
        map.beforeColumn,
        map.afterColumn
      );
      if (f && f.valueRows.length > 0) {
        patch.tTestNPerGroup = clampKeisukeGroupObservationCount(f.valueRows.length);
      }
    } else if (headers.length <= 2) {
      const gCol = map.groupColumn ?? headers[0];
      const vCol = map.valueColumn ?? headers[1];
      const f = gridDataToTTestTwoColumnForm(data, headers, gCol, vCol);
      if (f && f.valueRows.length > 0) {
        patch.tTestNPerGroup = clampKeisukeGroupObservationCount(f.valueRows.length);
      }
    }
  } else if (st === 'anova') {
    if (opt?.anovaType === 'twoWay') {
      const fa = map.factorAColumn ?? headers[0];
      const fb = map.factorBColumn ?? headers[1];
      const vc = map.valueColumn ?? headers[2];
      if (fa && fb && vc) {
        const nInfer = inferTwoWayNPerCellFromLongData(data, fa, fb);
        const form = gridDataToTwoWayBlockForm(data, headers, fa, fb, vc, nInfer);
        if (form && form.blocks.length > 0) {
          const aSet = new Set(form.blocks.map((b) => b.factorALabel));
          const bSet = new Set(form.blocks.map((b) => b.factorBLabel));
          patch.twoWayNLevelsA = clampKeisukeOneWayGroupCount(Math.max(2, aSet.size));
          patch.twoWayNLevelsB = clampKeisukeOneWayGroupCount(Math.max(2, bSet.size));
          patch.twoWayNPerCell = clampKeisukeGroupObservationCount(form.nPerCell);
        }
      }
    } else if (opt?.anovaType === 'repeatedMeasures') {
      const sCol = map.subjectColumn ?? headers[0];
      const fCol = map.factorAColumn ?? headers[2];
      const vCol = map.valueColumn ?? headers[3];
      const f = gridDataToRepeatedColumnForm(data, headers, sCol, fCol, vCol);
      if (f && f.valueRows.length > 0) {
        patch.repeatedNRows = clampKeisukeGroupObservationCount(f.valueRows.length);
      }
    } else {
      const gCol = map.groupColumn ?? map.factorAColumn ?? headers[0];
      const vCol = map.valueColumn ?? headers[1];
      const f = gridDataToOneWayColumnForm(data, headers, gCol, vCol);
      if (f && f.columnLabels.length > 0) {
        patch.oneWayGroupCount = clampKeisukeOneWayGroupCount(Math.max(2, f.columnLabels.length));
        patch.oneWayNPerGroup = clampKeisukeGroupObservationCount(
          Math.max(1, f.valueRows.length)
        );
      }
    }
  } else if (st === 'dose-response') {
    const dCol = map.doseColumn ?? headers[0];
    const eCol = map.effectColumn ?? headers[1];
    const tCol = map.totalColumn ?? headers[2];
    const f = gridDataToDoseResponseColumnForm(data, headers, dCol, eCol, tCol);
    if (f && f.valueRows.length > 0) {
      patch.doseResponseNRows = clampKeisukeGroupObservationCount(f.valueRows.length);
    }
  } else if (st === 'survival') {
    const gCol = map.survivalGroupColumn ?? map.groupColumn ?? headers[0];
    const tCol = map.timeColumn ?? headers[1];
    const eCol = map.eventColumn ?? headers[2];
    const f = gridDataToSurvivalColumnForm(data, headers, gCol, tCol, eCol);
    if (f && f.valueRows.length > 0) {
      patch.survivalNRows = clampKeisukeGroupObservationCount(f.valueRows.length);
    }
  }

  return patch;
}

/** 用量反応用 3列フォーム（用量・反応数・総数） */
export interface DoseResponseColumnForm {
  doseHeader: string;
  effectHeader: string;
  totalHeader: string;
  /** 各行 [用量, 反応数, 総数]。空セルはスキップ */
  valueRows: [string, string, string][];
}

const DEFAULT_DOSE_HEADER = 'Dose';
const DEFAULT_EFFECT_HEADER = 'Effect';
const DEFAULT_TOTAL_HEADER = 'Total';

export function doseResponseColumnFormToGridData(form: DoseResponseColumnForm): {
  headers: string[];
  data: DataRow[];
} {
  const doseH = trimLabel(form.doseHeader, DEFAULT_DOSE_HEADER);
  const effectH = trimLabel(form.effectHeader, DEFAULT_EFFECT_HEADER);
  const totalH = trimLabel(form.totalHeader, DEFAULT_TOTAL_HEADER);
  const headers = [doseH, effectH, totalH];
  const data: DataRow[] = [];
  form.valueRows.forEach(([dose, effect, total]) => {
    const d = parseNumber(dose);
    const e = parseNumber(effect);
    const t = parseNumber(total);
    if (d === null) return;
    const row: DataRow = { [doseH]: d };
    if (e !== null) row[effectH] = e;
    if (t !== null) row[totalH] = t;
    if (e !== null || t !== null) data.push(row);
  });
  return { headers, data };
}

export function gridDataToDoseResponseColumnForm(
  data: DataRow[],
  headers: string[],
  doseCol?: string,
  effectCol?: string,
  totalCol?: string
): DoseResponseColumnForm | null {
  const dCol = doseCol ?? headers[0];
  const eCol = effectCol ?? headers[1];
  const tCol = totalCol ?? headers[2];
  if (!dCol || !eCol) return null;
  const trimmed = trimTrailingEmptyGridRows(headers, data);
  const valueRows: [string, string, string][] = trimmed.map((r) => [
    r[dCol] != null && String(r[dCol]).trim() !== '' ? String(r[dCol]) : '',
    eCol && r[eCol] != null && String(r[eCol]).trim() !== '' ? String(r[eCol]) : '',
    tCol && r[tCol] != null && String(r[tCol]).trim() !== '' ? String(r[tCol]) : '',
  ]);
  if (valueRows.length === 0) return null;
  const hasAnyCell = valueRows.some((row) =>
    row.some((c) => String(c ?? '').trim() !== '')
  );
  if (!hasAnyCell) return null;
  return {
    doseHeader: dCol,
    effectHeader: eCol,
    totalHeader: tCol ?? DEFAULT_TOTAL_HEADER,
    valueRows,
  };
}

/** 生存曲線用 3列フォーム（群・時間・イベント） */
export interface SurvivalColumnForm {
  groupHeader: string;
  timeHeader: string;
  eventHeader: string;
  valueRows: [string, string, string][];
}

const DEFAULT_SURVIVAL_GROUP_HEADER = 'Group';
const DEFAULT_SURVIVAL_TIME_HEADER = 'Time';
const DEFAULT_SURVIVAL_EVENT_HEADER = 'Event';

export function survivalColumnFormToGridData(form: SurvivalColumnForm): {
  headers: string[];
  data: DataRow[];
} {
  const groupH = trimLabel(form.groupHeader, DEFAULT_SURVIVAL_GROUP_HEADER);
  const timeH = trimLabel(form.timeHeader, DEFAULT_SURVIVAL_TIME_HEADER);
  const eventH = trimLabel(form.eventHeader, DEFAULT_SURVIVAL_EVENT_HEADER);
  const headers = [groupH, timeH, eventH];
  const data: DataRow[] = [];
  form.valueRows.forEach(([group, time, event]) => {
    const g = group.trim();
    const tNum = parseNumber(time);
    const eNum = parseNumber(event);
    if (!g && tNum === null && eNum === null) return;
    const row: DataRow = {};
    if (g) row[groupH] = g;
    if (tNum !== null) row[timeH] = tNum;
    if (eNum !== null) row[eventH] = eNum;
    if (g || tNum !== null || eNum !== null) data.push(row);
  });
  return { headers, data };
}

export function gridDataToSurvivalColumnForm(
  data: DataRow[],
  headers: string[],
  groupCol?: string,
  timeCol?: string,
  eventCol?: string
): SurvivalColumnForm | null {
  const gCol = groupCol ?? headers[0];
  const tCol = timeCol ?? headers[1];
  const eCol = eventCol ?? headers[2];
  if (!gCol || !tCol || !eCol) return null;
  const trimmed = trimTrailingEmptyGridRows(headers, data);
  const valueRows: [string, string, string][] = trimmed.map((r) => [
    r[gCol] != null && String(r[gCol]).trim() !== '' ? String(r[gCol]) : '',
    r[tCol] != null && String(r[tCol]).trim() !== '' ? String(r[tCol]) : '',
    r[eCol] != null && String(r[eCol]).trim() !== '' ? String(r[eCol]) : '',
  ]);
  if (valueRows.length === 0) return null;
  const hasAnyCell = valueRows.some((row) =>
    row.some((c) => String(c ?? '').trim() !== '')
  );
  if (!hasAnyCell) return null;
  return {
    groupHeader: gCol,
    timeHeader: tCol,
    eventHeader: eCol,
    valueRows,
  };
}

/** 反復測定用 3列フォーム（被験体・因子・値） */
export interface RepeatedColumnForm {
  subjectHeader: string;
  factorHeader: string;
  valueHeader: string;
  valueRows: [string, string, string][];
}

const DEFAULT_REPEATED_SUBJECT_HEADER = 'SubjectID';
const DEFAULT_REPEATED_FACTOR_HEADER = 'Week';
const DEFAULT_REPEATED_VALUE_HEADER = 'Weight';

export function repeatedColumnFormToGridData(form: RepeatedColumnForm): {
  headers: string[];
  data: DataRow[];
} {
  const subjectH = trimLabel(form.subjectHeader, DEFAULT_REPEATED_SUBJECT_HEADER);
  const factorH = trimLabel(form.factorHeader, DEFAULT_REPEATED_FACTOR_HEADER);
  const valueH = trimLabel(form.valueHeader, DEFAULT_REPEATED_VALUE_HEADER);
  const headers = [subjectH, factorH, valueH];
  const data: DataRow[] = [];
  form.valueRows.forEach(([subject, factor, value]) => {
    const s = subject.trim();
    const f = factor.trim();
    const fNum = parseNumber(f);
    const vNum = parseNumber(value);
    if (!s && !f && vNum === null) return;
    const row: DataRow = {};
    if (s) row[subjectH] = s;
    if (f) row[factorH] = fNum !== null ? fNum : f;
    if (vNum !== null) row[valueH] = vNum;
    if (s || f || vNum !== null) data.push(row);
  });
  return { headers, data };
}

export function gridDataToRepeatedColumnForm(
  data: DataRow[],
  headers: string[],
  subjectCol?: string,
  factorCol?: string,
  valueCol?: string
): RepeatedColumnForm | null {
  const sCol = subjectCol ?? headers[0];
  const fCol = factorCol ?? headers[1];
  const vCol = valueCol ?? headers[2];
  if (!sCol || !fCol || !vCol) return null;
  const trimmed = trimTrailingEmptyGridRows(headers, data);
  const valueRows: [string, string, string][] = trimmed.map((r) => [
    r[sCol] != null && String(r[sCol]).trim() !== '' ? String(r[sCol]) : '',
    r[fCol] != null && String(r[fCol]).trim() !== '' ? String(r[fCol]) : '',
    r[vCol] != null && String(r[vCol]).trim() !== '' ? String(r[vCol]) : '',
  ]);
  if (valueRows.length === 0) return null;
  const hasAnyCell = valueRows.some((row) =>
    row.some((c) => String(c ?? '').trim() !== '')
  );
  if (!hasAnyCell) return null;
  return {
    subjectHeader: sCol,
    factorHeader: fCol,
    valueHeader: vCol,
    valueRows,
  };
}
